'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { MatchState, defaultMatchState, TeamId, TEAMS, WsMessage } from '@/types/match'

export type AllMatchStates = Record<string, MatchState>

const FIXED_TEAM_IDS = Object.keys(TEAMS) as TeamId[]

function makeDefaultStates(): AllMatchStates {
  return Object.fromEntries(
    FIXED_TEAM_IDS.map((id) => [id, { ...defaultMatchState, matchId: id }])
  )
}

/**
 * Hook che:
 * 1. Carica lo stato iniziale da GET /api/live/state
 * 2. Si connette al WebSocket e sottoscrive tutti i match
 * 3. Aggiorna lo stato in real-time sui messaggi in arrivo
 * 4. Riconnette automaticamente con backoff esponenziale
 */
export function useAllLiveMatches() {
  const [states, setStates] = useState<AllMatchStates>(makeDefaultStates)
  const [connected, setConnected] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const wsRef        = useRef<WebSocket | null>(null)
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const attemptRef   = useRef(0)
  const unmountedRef = useRef(false)

  const extraIdsRef = useRef<string[]>([])

  // ── Carica / ricarica stato dal DB ───────────────────────────────────────
  const fetchState = useCallback(() => {
    fetch('/api/live/state')
      .then((r) => r.json())
      .then((data: AllMatchStates) => {
        if (unmountedRef.current) return
        setStates((prev) => ({ ...prev, ...data }))
        const extras = Object.keys(data).filter(
          (id) => !FIXED_TEAM_IDS.includes(id as TeamId)
        )
        extraIdsRef.current = extras
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          for (const id of extras) {
            wsRef.current.send(JSON.stringify({ action: 'subscribe', matchId: id }))
          }
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!unmountedRef.current) setLoaded(true)
      })
  }, [])

  useEffect(() => { fetchState() }, [fetchState])

  // ── Refresh forzato dopo reset (evento browser emesso dalla dashboard) ────
  useEffect(() => {
    const handler = () => fetchState()
    window.addEventListener('live:force-refresh', handler)
    return () => window.removeEventListener('live:force-refresh', handler)
  }, [fetchState])

  // ── Connessione WebSocket ─────────────────────────────────────────────────
  const connect = useCallback(() => {
    if (unmountedRef.current) return

    const wsUrl = process.env.NEXT_PUBLIC_WS_URL
    if (!wsUrl) return

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      if (unmountedRef.current) { ws.close(); return }
      const isReconnect = attemptRef.current > 0
      attemptRef.current = 0
      setConnected(true)
      // Sottoscrivi team fissi + eventuali extra già caricati
      for (const teamId of [...FIXED_TEAM_IDS, ...extraIdsRef.current]) {
        ws.send(JSON.stringify({ action: 'subscribe', matchId: teamId }))
      }
      // Risincronizza stato dopo una disconnessione per recuperare gli aggiornamenti persi
      if (isReconnect) fetchState()
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as WsMessage | { type: 'ping'; timestamp: string }

        if (msg.type === 'ping') {
          ws.send(JSON.stringify({ action: 'ping' }))
          return
        }

        if (msg.type === 'score_update' && msg.matchId) {
          const payload = msg.payload as Partial<MatchState>
          setStates((prev) => ({
            ...prev,
            [msg.matchId]: { ...prev[msg.matchId as TeamId], ...payload },
          }))
        }
      } catch {/* ignora messaggi malformati */}
    }

    ws.onclose = () => {
      if (unmountedRef.current) return
      setConnected(false)
      wsRef.current = null
      // Backoff esponenziale: 1s, 2s, 4s, 8s, 16s (max)
      const delay = Math.min(1000 * 2 ** attemptRef.current, 16000)
      attemptRef.current++
      reconnectRef.current = setTimeout(connect, delay)
    }

    ws.onerror = () => { ws.close() }
  }, [])

  useEffect(() => {
    unmountedRef.current = false
    connect()
    return () => {
      unmountedRef.current = true
      if (reconnectRef.current) clearTimeout(reconnectRef.current)
      wsRef.current?.close()
    }
  }, [connect])

  return { states, connected, loaded }
}

/**
 * Variante single-match (per pannello OBS o operator dashboard).
 */
export function useLiveMatch(matchId: string) {
  const { states, connected, loaded } = useAllLiveMatches()
  return { state: states[matchId] ?? null, connected, loaded }
}

/**
 * Restituisce tutti gli ID di partita ordinati per position (con extra in coda).
 * Centralizza la logica di ordinamento usata da LiveViewer, LiveMobileViewer e LivePhoneMockup.
 */
export function useSortedMatchIds(states: AllMatchStates): string[] {
  return [
    ...FIXED_TEAM_IDS,
    ...Object.keys(states).filter((id) => !FIXED_TEAM_IDS.includes(id as TeamId)),
  ].sort((a, b) => {
    const pa = states[a]?.position ?? FIXED_TEAM_IDS.indexOf(a as TeamId) + 1000
    const pb = states[b]?.position ?? FIXED_TEAM_IDS.indexOf(b as TeamId) + 1000
    return pa - pb
  })
}
